import { useEffect } from 'react';

import {
  SNACK_STICKY_FLOOR_ID,
  STICKY_FLOOR_HEIGHT_PROPERTY,
  getStickyFloorHeight
} from '@lib/ads/snack';

/**
 * Keeps STICKY_FLOOR_HEIGHT_PROPERTY on <html> equal to the height of Snack's
 * floor ad while it shows an ad, and 0px otherwise, so the page and anything
 * fixed to the bottom of the viewport can make room for it (FFH-3482).
 *
 * The wrapper adds the floor on its own schedule, so this watches <body> for
 * it, then the floor itself: its class says whether an ad has rendered, and
 * its size changes when the ad resizes or the visitor closes it.
 */
export const useSnackStickyFloorSpace = (isActive: boolean) => {
  useEffect(() => {
    if (!isActive) return;

    const root = document.documentElement;
    let observedFloor: HTMLElement | null = null;

    const updateFloorHeight = () => {
      const floorHeight = getStickyFloorHeight(
        document.getElementById(SNACK_STICKY_FLOOR_ID)
      );
      root.style.setProperty(STICKY_FLOOR_HEIGHT_PROPERTY, `${floorHeight}px`);
    };

    const resizeObserver = new ResizeObserver(updateFloorHeight);
    const classObserver = new MutationObserver(updateFloorHeight);

    const watchFloor = () => {
      const floor = document.getElementById(SNACK_STICKY_FLOOR_ID);

      if (floor !== observedFloor) {
        resizeObserver.disconnect();
        classObserver.disconnect();
        observedFloor = floor;

        if (floor) {
          resizeObserver.observe(floor);
          classObserver.observe(floor, {
            attributes: true,
            attributeFilter: ['class', 'style']
          });
        }
      }

      updateFloorHeight();
    };

    // The wrapper appends the floor's container straight to <body>, so its
    // direct children are enough to notice it arriving or going.
    const bodyObserver = new MutationObserver(watchFloor);
    bodyObserver.observe(document.body, { childList: true });
    watchFloor();

    return () => {
      bodyObserver.disconnect();
      resizeObserver.disconnect();
      classObserver.disconnect();
      root.style.removeProperty(STICKY_FLOOR_HEIGHT_PROPERTY);
    };
  }, [isActive]);
};
